import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';
import Input from 'reactstrap/lib/Input';
import Form from 'reactstrap/lib/Form';
import Label from 'reactstrap/lib/Label';
import Button from 'reactstrap/lib/Button';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';

import './keyword-input.scss';

const INPUT_ID = 'keyword-search-input';
const PLACEHOLDER = 'Filter features (e.g. Exterior Colors)';

export function KeywordInput({ className, labelClassName, keyword, setKeyword, creativeId }) {
  const [isDisabled, setDisabled] = useState(true);
  const inputRef = useRef();
  const isInputDisabled = isDisabled && !keyword;

  useEffect(() => {
    inputRef.current.value = keyword;
  }, [keyword]);

  function onSubmit(event) {
    event.preventDefault();

    if (isInputDisabled) {
      return;
    }

    setKeyword(get(event, ['target', INPUT_ID, 'value'], ''));
  }

  function onInputChange({ target: { value } }) {
    setDisabled(!value.length);
  }

  function onInputFocus() {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_START,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_name: TrackingConstant.VIEW_SEARCH_RESULT,
        subaction_name: TrackingConstant.SELECT_AUTOCOMPLETE,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        creative_id: creativeId,
        value: PLACEHOLDER,
      },
    });
  }

  function onInputBlur({ target: { value } }) {
    if (!value) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_name: TrackingConstant.VIEW_SEARCH_RESULT,
          subaction_name: TrackingConstant.ABANDON_AUTO_COMPLETE_RESULT,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          creative_id: creativeId,
          autocomplete_links: 'null',
        },
      });
    }
  }

  return (
    <Form className={classnames('keyword-input', className)} onSubmit={onSubmit} data-tracking-parent={creativeId}>
      <Label for={INPUT_ID} className={labelClassName}>
        Search by keyword
      </Label>
      <div className="input-wrapper pos-r">
        <Input
          innerRef={inputRef}
          id={INPUT_ID}
          name={INPUT_ID}
          className="px-2_5 rounded-8 medium"
          placeholder={PLACEHOLDER}
          onChange={onInputChange}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
        />
        <i className="icon-search ms-1 pos-a left-0 center-y" aria-hidden />
        <Button
          type="submit"
          color="blue-50"
          disabled={isInputDisabled}
          className="submit-btn pos-a right-0 center-y px-0_5 py-0_25 me-0_5 text-white border-0 rounded"
          aria-label="Submit search by keyword"
          style={{ height: '28px', width: '28px' }}
        >
          <i className="pos-a center-xy icon-arrow-right8" aria-hidden />
        </Button>
      </div>
    </Form>
  );
}

KeywordInput.propTypes = {
  setKeyword: PropTypes.func.isRequired,
  keyword: PropTypes.string,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  creativeId: PropTypes.string,
};

KeywordInput.defaultProps = {
  keyword: null,
  className: null,
  labelClassName: 'heading-4 px-0_75 mb-1',
  creativeId: null,
};
